import React, { useState } from 'react';
import { api } from '../../App';
import { toast } from 'sonner';
import { Info, Sparkles, X, Loader2 } from 'lucide-react';

export function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);

  return (
    <span 
      className="relative inline-flex items-center gap-1 cursor-help"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={() => setShow(!show)}
    >
      {children}
      <Info className="w-4 h-4 text-slate-400 hover:text-accent transition-colors" />
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-primary text-white text-sm rounded-lg shadow-lg z-50 w-72 text-left animate-fade-in">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-primary"></span>
        </span>
      )}
    </span>
  );
}

export function AIAssistButton({ topic, onExplanation }) {
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const generateExplanation = async () => {
    if (explanation) {
      setShowExplanation(!showExplanation);
      return;
    }

    setLoading(true);
    try {
      const prompt = `Explain the ESG materiality issue "${topic}" for IT-BPM companies in the Philippines. Include:
1. A clear definition (2-3 sentences)
2. Why it matters for IT-BPM industry (2-3 key points)
3. Concrete examples relevant to BPO/IT companies
4. Tips on how to assess relevance if uncertain (2-3 actionable tips)
Keep it concise and practical for survey respondents.`;

      const res = await api.post('/api/advisor/chat', { message: prompt, session_id: null });
      setExplanation(res.data.response);
      setShowExplanation(true);
      if (onExplanation) onExplanation(res.data.response);
    } catch (error) {
      toast.error('Failed to generate explanation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={generateExplanation}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent bg-accent/10 hover:bg-accent/20 rounded-full transition-all disabled:opacity-50"
        data-testid={`ai-assist-${topic.replace(/\s+/g, '-').toLowerCase()}`}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" />
        )}
        AI Assist
      </button>

      {showExplanation && explanation && (
        <div className="mt-4 p-4 bg-gradient-to-br from-accent/5 to-emerald-50 border border-accent/20 rounded-xl animate-fade-in">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-sm font-semibold text-accent">AI Explanation</span>
            </div>
            <button onClick={() => setShowExplanation(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
            {explanation}
          </div>
        </div>
      )}
    </div>
  );
}

export function RatingScale({ value, onChange, label, tooltip }) {
  return (
    <div className="space-y-2">
      <Tooltip text={tooltip}>
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </Tooltip>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={`rating-option ${value === num ? 'selected' : ''}`}
            data-testid={`rating-${num}`}
          >
            {num}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-400">
        <span>Low</span>
        <span>High</span>
      </div>
    </div>
  );
}
